/**
 * Step-up MFA HTTP interceptor.
 *
 * Watches every outbound HTTP response for the canonical
 * `403 MFA_STEP_UP_REQUIRED` shape emitted by
 * `ApiAuthService.requireFreshMfa` (see @gozem-api/security). On a
 * match:
 *
 *   1. Ask `StepUpService` to open the challenge modal + wait for
 *      the operator to submit a fresh TOTP / recovery code.
 *   2. On `{status: "ok"}` — the service has already swapped the
 *      stored admin JWT via `AuthService.acceptMfaVerifiedToken`.
 *      Replay the ORIGINAL request unchanged; `authInterceptor`
 *      attaches the new token on the retry.
 *   3. On `{status: "cancelled"}` or `{status: "not_enrolled"}` —
 *      forward the original 403 to the caller so the caller's
 *      error branch runs (usually: surface a toast, revert
 *      optimistic UI). `not_enrolled` also triggers a route to
 *      `/mfa/enroll` inside StepUpService.
 *
 * Ordering — MUST come AFTER `authInterceptor` in
 * `app.config.ts` so:
 *   • The original request goes out with the current (possibly
 *     stale) token → server responds 403.
 *   • The retry goes out with the FRESH token
 *     (`authInterceptor` reads `AuthService.token()` at each
 *     request tick, so the retry picks up the swap).
 *
 * Idempotency + safety guards:
 *   • Only kicks in for `status === 403` AND
 *     `body.code === "MFA_STEP_UP_REQUIRED"` — everything else
 *     falls through, including plain 403 permission denials.
 *   • Guards against infinite loops: if the retry itself returns
 *     the same shape, we DON'T re-open the modal — we forward the
 *     error to the caller. The token swap must have failed
 *     server-side; better to surface it than spin.
 *   • The step-up endpoint (`POST /mfa/step-up`) is excluded from
 *     the interceptor by URL match — otherwise a stale token on
 *     the step-up call itself would recurse.
 *   • `/mfa/challenge`, `/mfa/enroll*` are also excluded — those
 *     have their own dedicated verify flows.
 */
import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from "@angular/common/http";
import {inject} from "@angular/core";
import {catchError, from, Observable, switchMap, throwError} from "rxjs";
import {StepUpService} from "../services/step-up.service";

// URL suffixes that MUST NOT trigger the step-up flow — they either
// ARE the step-up (recursion guard) or have their own bespoke
// verify UI.
const STEP_UP_EXCLUDED = ["/mfa/step-up", "/mfa/challenge", "/mfa/enroll", "/mfa/enroll/verify"];

function isExcluded(url: string): boolean {
    return STEP_UP_EXCLUDED.some((suffix) => url.endsWith(suffix));
}

function isStepUpRequired(err: HttpErrorResponse): boolean {
    if (err.status !== 403) {
        return false;
    }
    const body = err.error as {code?: string} | undefined;
    return body?.code === "MFA_STEP_UP_REQUIRED";
}

export const stepUpInterceptor: HttpInterceptorFn = (req, next) => {
    if (isExcluded(req.url)) {
        return next(req);
    }

    const stepUp = inject(StepUpService);

    return next(req).pipe(
        catchError((err) => {
            if (!(err instanceof HttpErrorResponse) || !isStepUpRequired(err)) {
                return throwError(() => err);
            }

            // Kick off the modal — the promise coalesces parallel
            // 403s onto the same challenge (see StepUpService).
            return from(stepUp.request()).pipe(
                switchMap((resolution) => {
                    if (resolution.status !== "ok") {
                        // Cancelled or not_enrolled — forward the
                        // original 403 so the caller surfaces it.
                        return throwError(() => err);
                    }
                    // Success — replay the ORIGINAL request. Any
                    // interceptor ordering that runs authInterceptor
                    // BEFORE this one will attach the new token.
                    // We DON'T set a re-entry flag here — the retry
                    // hits the interceptor chain fresh; if the
                    // backend returns MFA_STEP_UP_REQUIRED a second
                    // time it means the step-up itself didn't take,
                    // and re-opening the modal is preferable to
                    // silently swallowing.
                    return retryOnce(next, req);
                })
            );
        })
    );
};

// Retry a request once. Wraps the replay in a plain observable so we
// can add per-retry telemetry later without touching the interceptor
// core (`_flow` markers, structured logs, etc.). If the retry itself
// 403s with MFA_STEP_UP_REQUIRED, forward the error — a second modal
// would confuse the operator (their code just worked, but the
// backend still says stale? something else is broken).
function retryOnce(next: HttpHandlerFn, req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    return next(req).pipe(
        catchError((err) => {
            if (err instanceof HttpErrorResponse && isStepUpRequired(err)) {
                console.warn("[step-up] retry after successful step-up still returned MFA_STEP_UP_REQUIRED", {
                    url: req.urlWithParams
                });
            }
            return throwError(() => err);
        })
    );
}
